import { Menu } from 'lucide-react';
import { useSidebar } from './SidebarContext';
import { Link, useLocation } from 'react-router-dom';
import { UkropTip } from '../tooltips/DiagramTooltipContext';
import { BrandMark } from '../brand/BrandMark';
import { useAuth } from '../auth/AuthContext';
import { useUserUiSettings } from '../auth/userSettings';
import { resolveSiteTitle, siteIdFromPath } from './siteNav';
import { useDiagramChrome } from './DiagramChromeContext';
import { DiagramSearchField } from '../components/DiagramSearchField';
import { SIM_SCENARIO_LABELS_UK, SIM_SCENARIO_ORDER } from '../simulator/labels';
import type { SimScenario } from '../simulator/scenarios';

function initials(name: string | null, email: string | null): string {
  const source = (name || email || 'U').trim();
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const letters = (parts[0]?.[0] || 'U') + (parts[1]?.[0] || '');
  return letters.toUpperCase();
}

function CloudSyncBadge() {
  const { user, syncState, syncError, refreshWorkspace, persistWorkspace } = useAuth();
  if (!user) return null;
  const label =
    syncState === 'loading'
      ? 'Хмара…'
      : syncState === 'saving'
        ? 'Збереження…'
        : syncState === 'error'
          ? 'Хмара: помилка'
          : syncState === 'dirty'
            ? 'Не збережено'
            : 'Хмара';
  const hint =
    syncState === 'error'
      ? syncError === 'too-large'
        ? 'Схему не вдалося зберегти: занадто великий файл (фото або історія версій). Натисніть, щоб повторити.'
        : syncError === 'timeout'
          ? 'Сервер не відповів вчасно. Схема лишилась у браузері. Натисніть, щоб повторити збереження в хмару.'
          : syncError === 'download-failed' || (syncError?.startsWith('workspace-get') ?? false)
          ? `Не вдалося завантажити схему: ${syncError}. Натисніть, щоб повторити.`
          : syncError
            ? `Не вдалося зберегти на сервер: ${syncError}. Натисніть, щоб повторити.`
            : 'Не вдалося зберегти схему на сервер. Натисніть, щоб повторити.'
      : syncState === 'dirty'
        ? 'Зміни лише в браузері. Натисніть «Зберегти» на стрічці або тут, щоб записати в хмару.'
        : 'У хмару схема пишеться лише кнопкою «Зберегти» або при виході.';
  const className =
    syncState === 'error'
      ? 'focus-ring inline-flex min-h-11 items-center rounded-full bg-error-50 px-3 text-sm font-medium text-error-500 dark:bg-error-500/15'
      : syncState === 'saving' || syncState === 'loading' || syncState === 'dirty'
        ? 'focus-ring inline-flex min-h-11 items-center rounded-full bg-warning-50 px-3 text-sm font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400'
        : 'focus-ring inline-flex min-h-11 items-center rounded-full bg-success-50 px-3 text-sm font-medium text-success-600 dark:bg-success-500/15';
  const onClick = () => {
    if (syncState === 'dirty' || (syncState === 'error' && syncError !== 'download-failed' && !syncError?.startsWith('workspace-get'))) {
      void persistWorkspace({ immediate: true });
      return;
    }
    void refreshWorkspace();
  };
  return (
    <button type="button" className={className} title={hint} onClick={onClick}>
      {label}
    </button>
  );
}

function HeaderAccount() {
  const { user, logout } = useAuth();
  const { settings } = useUserUiSettings();
  if (!user) {
    return (
      <Link to="/login" className="btn-sm">
        Увійти
      </Link>
    );
  }
  const label = settings.displayName.trim() || user.name || user.email;
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-500 text-xs font-semibold text-white"
        title={user.email || label || 'Акаунт'}
      >
        {initials(label, user.email)}
      </span>
      <span className="hidden max-w-40 truncate text-sm text-gray-600 dark:text-gray-300 sm:inline">
        {label}
      </span>
      <button type="button" className="btn-sm" onClick={() => void logout()}>
        Вийти
      </button>
    </div>
  );
}

const titles: Record<string, string> = {
  '/': 'Обʼєкти моніторингу',
  '/settings': 'Налаштування',
};

const PRODUCT_TITLE = 'UKROP Network System';

export function AppHeader() {
  const { isOpen, toggle } = useSidebar();
  const location = useLocation();
  const chrome = useDiagramChrome()?.chrome ?? null;
  const onSite = location.pathname.startsWith('/site/');
  const siteId = siteIdFromPath(location.pathname);
  const siteTitle = siteId ? resolveSiteTitle(siteId) : null;
  const schemeName = chrome?.diagramName.trim() || siteTitle;
  const title = onSite ? PRODUCT_TITLE : (titles[location.pathname] ?? 'UKROP Network System');
  const subtitle = onSite
    ? schemeName || 'Схема'
    : 'UKROP Network Manager · приватний MVP';

  return (
    <header className="sticky top-0 z-30 flex w-full border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-2 md:flex-nowrap md:px-6">
        <div className="flex min-w-0 shrink items-center gap-3">
          <UkropTip text="Меню">
            <button
              type="button"
              className="btn-sm inline-flex items-center gap-1.5"
              onClick={toggle}
              aria-label={isOpen ? 'Закрити меню' : 'Відкрити меню'}
              aria-expanded={isOpen}
              aria-controls="app-sidebar"
              aria-haspopup="dialog"
            >
              <Menu size={16} strokeWidth={1.75} aria-hidden />
              <span className="hidden sm:inline">Меню</span>
            </button>
          </UkropTip>
          <span className="hidden sm:block">
            <BrandMark size={32} decorative />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-gray-800 md:text-lg dark:text-gray-100">
              {title}
            </h1>
            <p className="hidden truncate text-sm text-gray-600 dark:text-gray-300 sm:block" title={schemeName ?? subtitle}>
              {subtitle}
            </p>
          </div>
        </div>

        {chrome ? (
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            <label className="flex min-w-0 items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="hidden lg:inline">Симуляція</span>
              <select
                className="focus-ring min-h-11 max-w-48 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                value={chrome.scenario}
                onChange={(event) => chrome.onScenario(event.target.value as SimScenario)}
                aria-label="Сценарій симуляції"
              >
                {SIM_SCENARIO_ORDER.map((id) => (
                  <option key={id} value={id}>
                    {SIM_SCENARIO_LABELS_UK[id]}
                  </option>
                ))}
              </select>
            </label>
            <DiagramSearchField
              query={chrome.query}
              onQueryChange={chrome.onQueryChange}
              searchResults={chrome.searchResults}
              onPickSearch={chrome.onPickSearch}
            />
          </div>
        ) : (
          <div className="hidden flex-1 md:block" />
        )}

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <CloudSyncBadge />
          <span className="inline-flex min-h-11 items-center rounded-full bg-warning-50 px-3 text-sm font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
            LOCAL SIM
          </span>
          <HeaderAccount />
        </div>
      </div>
    </header>
  );
}
